import { useQuery } from '@tanstack/react-query';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { enrollmentApi, type PortalSectionDto } from '../../../lib/api/enrollment.api';
import { assignmentApi, type AssignmentSummaryDto } from '../../../lib/api/assignment.api';
import { courseTemplateApi, type CalendarEventDto } from '../../../lib/api/course-template.api';
import { lmsApi, type SectionStructureDto } from '../../../lib/api/lms.api';
import { programEnrollmentApi } from '../../../lib/api/program-enrollment.api';
import { useClassroomOn } from './use-classroom-on';
import { calendarEventsFor, scheduleCalendarEvents } from '../util/classroom';

/** Section enrollment + active term. */
export function useEnrolledSections() {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const { personId } = useStudentIdentity();
  return useQuery({
    queryKey: ['portal-v2-sections', currentTenantId, scope, personId],
    enabled: !!currentTenantId && !!personId,
    queryFn: () =>
      enrollmentApi.getSectionsForPortal({
        tenantId: currentTenantId!,
      }),
    staleTime: 60_000,
  });
}

/** Resolve a single section by id from the enrolled list. */
export function useSection(sectionId: string | undefined): PortalSectionDto | undefined {
  const { data } = useEnrolledSections();
  if (!sectionId) return undefined;
  return data?.enrolledSections.find((s) => s.id === sectionId);
}

/** All assignments across all enrolled sections (flattened). */
export function useAllAssignments() {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const { personId } = useStudentIdentity();
  const sectionsQ = useEnrolledSections();
  const sections = sectionsQ.data?.enrolledSections ?? [];

  return useQuery({
    queryKey: [
      'portal-v2-all-assignments',
      currentTenantId,
      scope,
      personId,
      sections.map((s) => s.id).join(','),
    ],
    enabled: !!currentTenantId && !!personId && sections.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        sections.map(async (s) => {
          const r = await assignmentApi.list({
            tenantId: currentTenantId!,
            sectionId: s.id,
            ...(personId ? { requestingStudentId: personId } : {}),
          });
          return r.items.map((a) => ({
            ...a,
            sectionId: s.id,
            courseName: s.courseName,
            courseCode: s.courseCode,
          }));
        }),
      );
      return results.flat();
    },
    staleTime: 30_000,
  });
}

export type FlatAssignment = AssignmentSummaryDto & {
  sectionId: string;
  courseName: string;
  courseCode: string;
};

/** Calendar events across all enrolled sections, given a date window. */
export function useAllCalendarEvents(fromDate: string, toDate: string, enabled = true) {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const sectionsQ = useEnrolledSections();
  const sections = sectionsQ.data?.enrolledSections ?? [];

  return useQuery({
    queryKey: [
      'portal-v2-calendar',
      currentTenantId,
      scope,
      sections.map((s) => s.id).join(','),
      fromDate,
      toDate,
    ],
    enabled: enabled && !!currentTenantId && sections.length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        sections.map(async (s) => {
          try {
            const r = await courseTemplateApi.getSectionCalendar({
              tenantId: currentTenantId!,
              sectionId: s.id,
              fromDate,
              toDate,
            });
            return r.items.map((evt) => ({
              ...evt,
              sectionId: s.id,
              courseName: s.courseName,
              courseCode: s.courseCode,
            }));
          } catch {
            return [];
          }
        }),
      );
      return results.flat().sort((a, b) => a.date.localeCompare(b.date));
    },
    staleTime: 60_000,
  });
}

export type FlatCalendarEvent = CalendarEventDto & {
  sectionId: string;
  courseName: string;
  courseCode: string;
  /** Solo en las clases que salen del horario de la sección (sin aula). */
  location?: string;
  startOnly?: boolean;
  source?: 'schedule';
};

/**
 * Lo que muestran el calendario y «Próximos 7 días».
 * - Con aula: las sesiones del syllabus y las tareas, como siempre.
 * - Sin aula (g4): las clases que salen del HORARIO de cada sección inscrita;
 *   no se le pide nada al syllabus. Ver `scheduleCalendarEvents`.
 */
export function usePortalCalendar(
  fromDate: string,
  toDate: string,
): { data: FlatCalendarEvent[]; isLoading: boolean; source: 'syllabus' | 'schedule' } {
  const classroom = useClassroomOn();
  const sectionsQ = useEnrolledSections();
  const syllabusQ = useAllCalendarEvents(fromDate, toDate, classroom.settled && classroom.on);
  if (!classroom.settled) return { data: [], isLoading: true, source: 'syllabus' };
  if (classroom.on) {
    return {
      data: calendarEventsFor(syllabusQ.data ?? [], true),
      isLoading: syllabusQ.isLoading,
      source: 'syllabus',
    };
  }
  return {
    data: scheduleCalendarEvents(sectionsQ.data, fromDate, toDate),
    isLoading: sectionsQ.isLoading,
    source: 'schedule',
  };
}

/** Module structure for a single section. */
export function useSectionStructure(sectionId: string | undefined) {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const { personId } = useStudentIdentity();
  return useQuery<SectionStructureDto>({
    queryKey: ['portal-v2-structure', currentTenantId, scope, sectionId, personId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () =>
      lmsApi.getSectionStructure({
        tenantId: currentTenantId!,
        sectionId: sectionId!,
        ...(personId ? { requestingStudentId: personId } : {}),
      }),
    staleTime: 60_000,
  });
}

/** Assignments for one section. */
export function useSectionAssignments(sectionId: string | undefined) {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const { personId } = useStudentIdentity();
  return useQuery({
    queryKey: ['portal-v2-section-assignments', currentTenantId, scope, sectionId, personId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () =>
      assignmentApi.list({
        tenantId: currentTenantId!,
        sectionId: sectionId!,
        ...(personId ? { requestingStudentId: personId } : {}),
      }),
    staleTime: 30_000,
  });
}

/** Long-term program memberships (ProgramEnrollment) for the current student. */
export function useStudentProgramEnrollments() {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const { personId } = useStudentIdentity();
  return useQuery({
    queryKey: ['portal-v2-program-enrollments', currentTenantId, scope, personId],
    enabled: !!currentTenantId && !!personId,
    queryFn: () =>
      programEnrollmentApi.listStudentProgramEnrollments({
        tenantId: currentTenantId!,
        studentId: personId!,
      }),
    staleTime: 60_000,
  });
}

/** Degree audit for the current student in a given program. */
export function useDegreeAudit(programId: string | undefined) {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const { personId } = useStudentIdentity();
  return useQuery({
    queryKey: ['portal-v2-degree-audit', currentTenantId, scope, personId, programId],
    enabled: !!currentTenantId && !!personId && !!programId,
    queryFn: () =>
      programEnrollmentApi.getDegreeAudit({
        tenantId: currentTenantId!,
        studentId: personId!,
        programId: programId!,
      }),
    staleTime: 60_000,
  });
}

import { userItemViewApi } from '../../../lib/api/user-item-view.api';
import { usePortalCacheScope } from '../../../stores/portal-view.store';

/** Fetch item views for current section from backend. */
export function useSectionItemViews(sectionId: string | undefined) {
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const { personId } = useStudentIdentity();
  return useQuery({
    queryKey: ['portal-v2-item-views', currentTenantId, scope, sectionId, personId],
    enabled: !!currentTenantId && !!sectionId,
    queryFn: () =>
      userItemViewApi.listMine({
        tenantId: currentTenantId!,
        sectionId: sectionId!,
      }),
    staleTime: 10_000,
  });
}
